"use client";

import { c as _c } from "react/compiler-runtime";
import type { FilterFormValues } from "@/components/filters/FilterDropdown";
import type { ExportMutation } from "@/components/list/ListDisplayControls";
import Pagination from "@/components/list/Pagination";
import { RouteReadyTelemetry } from "@/components/telemetry/RouteReadyTelemetry";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import type { Option } from "@/components/ui/Combobox";
import { ContextMenuItem, ContextMenuSeparator, ContextMenuShortcut } from "@/components/ui/ContextMenu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from "@/components/ui/DropdownMenu";
import { ScrollArea } from "@/components/ui/ScrollArea";
import { Separator } from "@/components/ui/Separator";
import { Spinner } from "@/components/ui/Spinner";
import type { PaginationResult } from "@/hooks/pagination";
import type { AnyLabeledFilterConfig, ListGroupingOption, ListPresentationConfig, ListPresentationDirection } from "@/types/list";
import type { ActionMenuEntry, ActionMenuItem } from "@/types/menu";
import { getColumnHeaderGroups } from "@/utils/column-header-groups";
import { downloadTableExport } from "@/utils/table-export-download.client";
import type { ColumnDef, Row, RowSelectionState, useReactTable } from "@tanstack/react-table";
import { Download, RotateCcw } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { toast } from "sonner";
import type { Returns } from "tanstack-table-search-params";
import cn from "twsx";
import type { ColumnGroupHeaderLayout } from "./ColumnGroupHeaders";
import styles from "./List.module.css";
import { FilterListGridSurface } from "./FilterListGridSurface";
import { FilterListHeader } from "./FilterListHeader";
import { FilterListRow } from "./FilterListRow";
import { ListGroupHeader } from "./ListGroupHeader";
import { ListHeaderControls } from "./ListHeaderControls";
import { ListSelectionBar, ListSelectionBarAction, ListSelectionBarGroup, ListSelectionHeader } from "./ListSelection";
import { isInteractiveListRowTarget } from "./listRowInteraction";
import { ListSurfaceState, useListViewportClientWidth } from "./ListSurfaceState";
const unpinnedScrollbarRegion = {
  container: "[data-list-table-header]",
  start: '[data-list-table-header] > [data-sticky-column="left"]',
  end: '[data-list-table-header] > [data-sticky-column="right"]'
};
export type SelectionTarget<T> = {
  type: "rows";
  rows: T[];
} | {
  type: "all";
};
export type FilterSelectActionInterface<T> = {
  label: string;
  /** Optional visual group key. Adjacent visible actions with the same key share
   * a section; changing keys adds a full-height divider. No group labels render.
   * Omit on existing actions to keep a single section.
   */
  group?: string;
  icon?: React.ReactNode;
  onClick: (target: SelectionTarget<T>, clearSelection?: () => void) => void;
  variant?: "default" | "primary" | "destructive";
  /** When false, this action is hidden when all results are selected. Default true. */
  supportsAll?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
};
export type FilterListPaginationState = {
  mode: "paged";
  count?: number;
  pagination: PaginationResult;
  pageSizeOptions?: number[];
};
export type FilterListStickyColumns = {
  left?: string[];
  right?: string[];
};
export type FilterListRowVariant = "default" | "summary";
type StickyColumnSide = "left" | "right";
export type ListColumnMeta = {
  grow?: boolean;
  columnGroupEnd?: boolean;
  align?: "left" | "right";
  /** Stacked cell content (a value over 12px meta lines) gets the row's vertical padding. */
  multiline?: boolean;
};
export type StickyColumnLayout = {
  side: StickyColumnSide;
  offset: string;
  isBoundary: boolean;
  width?: number;
};
export type HorizontalOverflowState = {
  hasScrolledLeft: boolean;
  hasScrollableRight: boolean;
};
export type FilterItem = {
  type: "select" | "multiselect" | "boolean";
  label: string;
  icon?: React.ReactNode;
  selectedValue?: string;
  itemType: "normal" | "profile-value" | "custom";
  options?: Option[];
  getOptions?: (search: string) => Promise<Option[]> | Option[];
  getCustomWhere?: (values: string[] | boolean) => Record<string, unknown>;
};
export type FilterListProps<$Row, $Filters extends FilterFormValues> = {
  data: Array<$Row>;
  computedData?: Array<Partial<$Row>>;
  columns: Array<ColumnDef<$Row>>;
  actionMenuItems?: (row: $Row) => ActionMenuEntry<$Row>[];
  actionMenuTriggerVariant?: "ghost" | "secondary";
  actionButtons?: (row: $Row) => React.ReactNode;
  actionButtonsDisplay?: "hover" | "sticky";
  actionColumnSize?: number;
  card?: (row: $Row) => React.ReactNode;
  selectActions?: FilterSelectActionInterface<$Row>[];
  isLoading?: boolean;
  isRefreshing?: boolean;
  error?: unknown;
  isRetrying?: boolean;
  onRetry?: () => void;
  onFetchMore?: () => void;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  filterConfigs?: Record<string, AnyLabeledFilterConfig>;
  filterInput?: Record<string, unknown>;
  filterComponents?: Record<string, React.ComponentType<any>>;
  onFilterChange?: (values: $Filters) => void;
  currentFilters?: $Filters;
  currentSearch?: string;
  toolbarPlacement?: "internal" | "external";
  surface?: "flush" | "framed";
  tableContentInset?: "default" | "list" | "compact";
  enableRowSelection?: boolean | ((row: Row<$Row>) => boolean);
  viewMode?: "table" | "grid";
  selectionScopeKey?: string;
  showSelectionControls?: boolean;
  presentationConfig?: ListPresentationConfig<$Row>;
  orderBy?: string | null;
  orderDirection?: "asc" | "desc";
  onOrderChange?: (orderBy: string | null, orderDirection: ListPresentationDirection) => void;
  manualSorting?: boolean;
  visibleColumnIds?: string[];
  groupingOption?: ListGroupingOption<$Row> | null;
  subGroupingOption?: ListGroupingOption<$Row> | null;
  listOptionState?: Record<string, boolean>;
  tableSearchParams?: Returns;
  exportMutation?: ExportMutation;
  immediateSelectedExport?: {
    isPending: boolean;
    download: (input: {
      format: "csv" | "xlsx" | "pdf";
      ids: string[];
    }) => Promise<void>;
  };
  countQuery?: {
    data?: number;
    isLoading: boolean;
  };
  pagination?: FilterListPaginationState;
  stickyColumns?: FilterListStickyColumns;
  horizontalScrollbarRegion?: "viewport" | "unpinned";
  flexibleColumnId?: string;
  columnHeaderGroups?: Readonly<Record<string, string>>;
  getRowVariant?: (row: $Row) => FilterListRowVariant;
  getRowStyle?: (row: $Row, rowIndex: number) => React.CSSProperties;
  isRowClickable?: (row: $Row) => boolean;
  getRowHref?: (row: $Row) => string | undefined;
  getRowTarget?: (row: $Row) => React.HTMLAttributeAnchorTarget | undefined;
  onClickRow?: (row: $Row) => void;
};
type FilterListGroupedRows<$Row> = Array<{
  key: string;
  label: string;
  rows: Array<Row<$Row>>;
  children?: FilterListGroupedRows<$Row>;
}>;
export function FilterListInternalToolbar(t0) {
  const $ = _c(11);
  const {
    filterConfigs,
    filterInput,
    filterComponents,
    onFilterChange,
    currentFilters,
    exportMutation
  } = t0;
  if (!filterConfigs && !exportMutation) {
    return null;
  }
  let t1;
  if ($[0] !== filterConfigs) {
    t1 = filterConfigs ?? {};
    $[0] = filterConfigs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== filterComponents) {
    t2 = filterComponents ?? {};
    $[2] = filterComponents;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== currentFilters || $[5] !== exportMutation || $[6] !== filterInput || $[7] !== onFilterChange || $[8] !== t1 || $[9] !== t2) {
    t3 = <div className="sticky top-0 z-10 flex w-full items-center border-b border-text-primary/10 bg-bg-surface px-6 py-2"><ListHeaderControls className="w-full" filterConfigs={t1} filterComponents={t2} filterInput={filterInput} currentFilters={currentFilters} onFilterChange={onFilterChange} exportMutation={exportMutation} wrapWithParent={false} /></div>;
    $[4] = currentFilters;
    $[5] = exportMutation;
    $[6] = filterInput;
    $[7] = onFilterChange;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function joinCssLengthParts(parts: string[]) {
  if (parts.length === 0) {
    return "0px";
  }
  if (parts.length === 1) {
    return parts[0] ?? "0px";
  }
  return `calc(${parts.join(" + ")})`;
}
function useHorizontalOverflowState(ref) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      hasScrolledLeft: false,
      hasScrollableRight: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [horizontalOverflowState, setHorizontalOverflowState] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== ref) {
    t1 = () => {
      const element = ref.current;
      if (!element) {
        return;
      }
      const updateHorizontalOverflowState = () => {
        const maxScrollLeft = Math.max(0, element.scrollWidth - element.clientWidth);
        element.style.setProperty("--list-scroll-left", `${element.scrollLeft}px`);
        const nextState = {
          hasScrolledLeft: element.scrollLeft > 0,
          hasScrollableRight: maxScrollLeft > 1 && element.scrollLeft < maxScrollLeft - 1
        };
        setHorizontalOverflowState(currentState => currentState.hasScrolledLeft === nextState.hasScrolledLeft && currentState.hasScrollableRight === nextState.hasScrollableRight ? currentState : nextState);
      };
      updateHorizontalOverflowState();
      element.addEventListener("scroll", updateHorizontalOverflowState, {
        passive: true
      });
      if (typeof ResizeObserver === "undefined") {
        window.addEventListener("resize", updateHorizontalOverflowState);
        return () => {
          element.removeEventListener("scroll", updateHorizontalOverflowState);
          window.removeEventListener("resize", updateHorizontalOverflowState);
          element.style.removeProperty("--list-scroll-left");
        };
      }
      const resizeObserver = new ResizeObserver(updateHorizontalOverflowState);
      resizeObserver.observe(element);
      element.querySelectorAll("[data-list-table-grid]").forEach(grid => resizeObserver.observe(grid));
      return () => {
        element.removeEventListener("scroll", updateHorizontalOverflowState);
        resizeObserver.disconnect();
        element.style.removeProperty("--list-scroll-left");
      };
    };
    t2 = [ref];
    $[1] = ref;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return horizontalOverflowState;
}
function useStickyColumnLayouts<$Row>({
  enableRowSelection,
  stickyColumns,
  table
}: {
  enableRowSelection: boolean;
  stickyColumns?: FilterListStickyColumns;
  table: ReturnType<typeof useReactTable<$Row>>;
}) {
  "use no memo";

  // TanStack's stable table handle exposes mutable columns; compiler caching can retain pre-load widths.
  const leftColumnIds = useMemo(() => new Set(stickyColumns?.left ?? []), [stickyColumns?.left]);
  const rightColumnIds = useMemo(() => new Set(stickyColumns?.right ?? []), [stickyColumns?.right]);
  const headers = table.getFlatHeaders();
  const columnSizing = table.getState().columnSizing;
  return useMemo(() => {
    const layouts = new Map<string, StickyColumnLayout>();
    const leftOffsetParts = ["var(--list-active-table-padding-inline)"];
    if (enableRowSelection) {
      leftOffsetParts.push("var(--list-selection-column-width)", "var(--list-row-gap)");
    }
    const leftPinnedHeaders = headers.filter(header => leftColumnIds.has(header.id));
    const leftBoundaryColumnId = leftPinnedHeaders.at(-1)?.id;
    leftPinnedHeaders.forEach(header_0 => {
      layouts.set(header_0.id, {
        side: "left",
        offset: joinCssLengthParts(leftOffsetParts),
        isBoundary: header_0.id === leftBoundaryColumnId,
        width: leftPinnedHeaders.length > 1 ? header_0.getSize() : undefined
      });
      leftOffsetParts.push(`${header_0.getSize()}px`, "var(--list-row-gap)");
    });
    const rightOffsetParts = ["var(--list-active-table-padding-inline)"];
    const rightPinnedHeaders = headers.filter(header_1 => rightColumnIds.has(header_1.id) && !layouts.has(header_1.id));
    const rightBoundaryColumnId = rightPinnedHeaders[0]?.id;
    [...rightPinnedHeaders].reverse().forEach(header_2 => {
      const rightOffset = joinCssLengthParts(rightOffsetParts);
      layouts.set(header_2.id, {
        side: "right",
        offset: rightOffset,
        isBoundary: header_2.id === rightBoundaryColumnId
      });
      rightOffsetParts.push(`${header_2.getSize()}px`, "var(--list-row-gap)");
    });
    return layouts;
  }, [columnSizing, enableRowSelection, headers, leftColumnIds, rightColumnIds]);
}
function FilterListTableSurface<$Row>({
  table,
  enableRowSelection,
  getRowHref,
  getRowTarget,
  onClickRow,
  handleRowSelect,
  actionButtons,
  actionButtonsDisplay,
  actionColumnSize = 184,
  actionMenuTriggerVariant,
  actionMenuItems,
  isLoading,
  error,
  isRetrying,
  onRetry,
  bottomRef,
  viewportRef,
  isFetchingMore,
  groupingOption,
  subGroupingOption,
  listOptionState,
  selectAllBanner,
  isAllSelected,
  onClearSelection,
  stickyColumns,
  horizontalScrollbarRegion,
  flexibleColumnId,
  columnHeaderGroups,
  getRowVariant,
  getRowStyle,
  isRowClickable,
  surface,
  tableContentInset
}: {
  table: ReturnType<typeof useReactTable<$Row>>;
  enableRowSelection: boolean;
  getRowHref?: (row: $Row) => string | undefined;
  getRowTarget?: (row: $Row) => React.HTMLAttributeAnchorTarget | undefined;
  onClickRow?: (row: $Row) => void;
  handleRowSelect: (clickedRowIndex: number, shiftKey: boolean) => void;
  actionButtons?: (row: $Row) => React.ReactNode;
  actionButtonsDisplay?: "hover" | "sticky";
  actionColumnSize?: number;
  actionMenuTriggerVariant?: "ghost" | "secondary";
  actionMenuItems?: (row: $Row) => ActionMenuEntry<$Row>[];
  isLoading?: boolean;
  error?: unknown;
  isRetrying?: boolean;
  onRetry?: () => void;
  bottomRef: RefObject<HTMLDivElement>;
  viewportRef: RefObject<HTMLDivElement>;
  isFetchingMore?: boolean;
  groupingOption?: ListGroupingOption<$Row> | null;
  subGroupingOption?: ListGroupingOption<$Row> | null;
  listOptionState?: Record<string, boolean>;
  selectAllBanner?: ReactNode;
  isAllSelected?: boolean;
  onClearSelection?: () => void;
  stickyColumns?: FilterListStickyColumns;
  horizontalScrollbarRegion?: "viewport" | "unpinned";
  flexibleColumnId?: string;
  columnHeaderGroups?: Readonly<Record<string, string>>;
  getRowVariant?: (row: $Row) => FilterListRowVariant;
  getRowStyle?: (row: $Row, rowIndex: number) => React.CSSProperties;
  isRowClickable?: (row: $Row) => boolean;
  surface: "flush" | "framed";
  tableContentInset: "default" | "list" | "compact";
}) {
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Record<string, boolean>>({});
  const viewportClientWidth = useListViewportClientWidth(viewportRef);
  const toggleGroup = useCallback((groupKey: string) => {
    setCollapsedGroupKeys(current => ({
      ...current,
      [groupKey]: !current[groupKey]
    }));
  }, []);
  const groupedRows = useMemo<FilterListGroupedRows<$Row> | null>(() => {
    if (!groupingOption) {
      return null;
    }
    const showEmptyGroups = listOptionState?.showEmptyGroups ?? false;
    function formatGroupValue(option: ListGroupingOption<$Row>, rawValue: unknown) {
      if (rawValue == null || rawValue === "") {
        return option.emptyLabel ?? "None";
      }
      return option.formatValue?.(rawValue as any) ?? String(rawValue);
    }
    function normalizeGroupValue(rawValue_0: unknown) {
      if (rawValue_0 instanceof Date) {
        return rawValue_0.toISOString();
      }
      return String(rawValue_0 ?? "");
    }
    function buildGroups(rows: Array<Row<$Row>>, option_0: ListGroupingOption<$Row>, childOption?: ListGroupingOption<$Row> | null, parentKey = option_0.key): FilterListGroupedRows<$Row> {
      const groups = new Map<string, FilterListGroupedRows<$Row>[number]>();
      rows.forEach(row => {
        const rawValue_1 = option_0.getValue(row.original);
        const normalizedValue = normalizeGroupValue(rawValue_1);
        const groupKey_0 = normalizedValue || "__empty__";
        const compositeKey = `${parentKey}:${groupKey_0}`;
        const currentGroup = groups.get(compositeKey);
        if (currentGroup) {
          currentGroup.rows.push(row);
          return;
        }
        groups.set(compositeKey, {
          key: compositeKey,
          label: formatGroupValue(option_0, rawValue_1),
          rows: [row]
        });
      });
      if (showEmptyGroups && option_0.valueOptions) {
        option_0.valueOptions.forEach(valueOption => {
          const normalizedValue_0 = normalizeGroupValue(valueOption.value);
          const groupKey_1 = normalizedValue_0 || "__empty__";
          const compositeKey_0 = `${parentKey}:${groupKey_1}`;
          if (groups.has(compositeKey_0)) {
            return;
          }
          groups.set(compositeKey_0, {
            key: compositeKey_0,
            label: valueOption.label ?? formatGroupValue(option_0, valueOption.value),
            rows: []
          });
        });
      }
      const resolvedGroups = Array.from(groups.values());
      if (!childOption) {
        return resolvedGroups;
      }
      return resolvedGroups.map(group => ({
        ...group,
        children: buildGroups(group.rows, childOption, null, group.key)
      }));
    }
    return buildGroups(table.getRowModel().rows, groupingOption, subGroupingOption);
  }, [groupingOption, listOptionState?.showEmptyGroups, subGroupingOption, table]);
  const rowIndexById = useMemo(() => new Map(table.getRowModel().rows.map((row_0, rowIndex) => [row_0.id, rowIndex])), [table]);

  // Build CSS grid-template-columns from TanStack table columns
  // TanStack keeps the table instance stable when async column definitions change.
  const headers = table.getFlatHeaders();
  const columnSizing = table.getState().columnSizing;
  const gridTemplateColumns = useMemo(() => {
    const cols: string[] = [];
    if (enableRowSelection) {
      cols.push("var(--list-selection-column-width)");
    }
    const stickyColumnIds = new Set([...(stickyColumns?.left ?? []), ...(stickyColumns?.right ?? [])]);
    const flexibleHeaderId = headers.find(header_0 => header_0.id === flexibleColumnId && (!stickyColumnIds.has(header_0.id) || stickyColumns?.left?.length === 1 && stickyColumns.left[0] === header_0.id))?.id ?? headers.find(header => !stickyColumnIds.has(header.id) && (header.column.columnDef.meta as ListColumnMeta | undefined)?.grow === true)?.id;
    const firstRightStickyHeaderId = headers.find(header_1 => stickyColumns?.right?.includes(header_1.id))?.id;
    const hasTrailingStickyColumn = Boolean(firstRightStickyHeaderId) || Boolean(actionButtons && actionButtonsDisplay === "sticky");
    const flexibleSpacerTrack = flexibleHeaderId ? "12px" : hasTrailingStickyColumn ? "minmax(0px, 1fr)" : "minmax(12px, 1fr)";
    headers.forEach(header_2 => {
      if (header_2.id === firstRightStickyHeaderId) {
        cols.push(flexibleSpacerTrack);
      }
      const size = header_2.getSize();
      if (header_2.id === flexibleHeaderId) {
        cols.push(`minmax(${size}px, 1fr)`);
      } else {
        cols.push(`${size}px`);
      }
    });
    if (!firstRightStickyHeaderId) {
      cols.push(flexibleSpacerTrack);
    }
    if (actionButtons && actionButtonsDisplay === "sticky") {
      cols.push(`${actionColumnSize}px`);
    }
    return cols.join(" ");
  }, [actionButtons, actionButtonsDisplay, actionColumnSize, columnSizing, enableRowSelection, flexibleColumnId, headers, stickyColumns?.left, stickyColumns?.right]);
  const getVisibleMenuItems = useCallback((row_1: $Row) => {
    if (!actionMenuItems) return [];
    return actionMenuItems(row_1).filter(entry => "type" in entry ? true : entry.isVisible !== false);
  }, [actionMenuItems]);
  const renderMenuEntries = useCallback((entries: ActionMenuEntry<$Row>[], row_2: $Row, variant: "dropdown" | "context") => {
    const MenuItem = variant === "context" ? ContextMenuItem : DropdownMenuItem;
    const Separator = variant === "context" ? ContextMenuSeparator : DropdownMenuSeparator;
    const Shortcut = variant === "context" ? ContextMenuShortcut : DropdownMenuShortcut;
    return entries.map((entry_0, index) => {
      if ("type" in entry_0 && entry_0.type === "separator") {
        return <Separator key={`sep-${index}`} />;
      }
      const item = entry_0 as ActionMenuItem<$Row>;
      if (item.isVisible === false) return null;
      return <MenuItem key={index} destructive={item.isDestructive} onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        // Let Radix close the menu and release its focus trap after selection.
        item.onClick(row_2);
      }}>
          {item.icon}
          {item.title}
          {item.shortcut && <Shortcut keys={item.shortcut} />}
        </MenuItem>;
    });
  }, []);

  // Stable click handlers — don't depend on selection state
  const onRowClick = useCallback((e_0: React.MouseEvent, row_3: Row<$Row>, rowIndex_0: number) => {
    if (isInteractiveListRowTarget(e_0.target)) {
      return;
    }
    if (enableRowSelection && row_3.getCanSelect() && e_0.shiftKey) {
      e_0.preventDefault();
      handleRowSelect(rowIndex_0, true);
      return;
    }
    onClickRow?.(row_3.original);
  }, [enableRowSelection, handleRowSelect, onClickRow]);
  const onCheckboxClick = useCallback((e_1: React.MouseEvent, rowIndex_1: number) => {
    e_1.stopPropagation();
    e_1.preventDefault();
    handleRowSelect(rowIndex_1, e_1.shiftKey);
  }, [handleRowSelect]);
  const hasClickHandler = !!onClickRow;
  const stickyColumnLayouts = useStickyColumnLayouts({
    enableRowSelection,
    stickyColumns,
    table
  });
  const horizontalOverflowState = useHorizontalOverflowState(viewportRef);
  const hasStickyLeftDataColumn = useMemo(() => [...stickyColumnLayouts.values()].some(layout => layout.side === "left"), [stickyColumnLayouts]);
  const showSelectionBoundary = !hasStickyLeftDataColumn && horizontalOverflowState.hasScrolledLeft;
  const renderRow = useCallback((row_4: Row<$Row>, rowIndex_2: number, isPreviousRowSelected = false, isNextRowSelected = false) => {
    const clickable = isRowClickable?.(row_4.original) ?? true;
    const href = clickable ? getRowHref?.(row_4.original) : undefined;
    const rowHasClickHandler = !href && hasClickHandler && clickable;
    const variant_0 = getRowVariant?.(row_4.original) ?? "default";
    const nextRow = table.getRowModel().rows[rowIndex_2 + 1];
    const nextVariant = nextRow ? getRowVariant?.(nextRow.original) ?? "default" : null;
    return <React.Fragment key={row_4.id}>
        {variant_0 === "summary" && <div aria-hidden="true" className={styles.clearance} />}
        <FilterListRow row={row_4} rowIndex={rowIndex_2} isSelected={row_4.getIsSelected()} isPreviousRowSelected={isPreviousRowSelected} isNextRowSelected={isNextRowSelected} enableRowSelection={enableRowSelection} href={href} target={getRowTarget?.(row_4.original)} hasClickHandler={rowHasClickHandler} onRowClick={onRowClick} onCheckboxClick={onCheckboxClick} actionButtons={actionButtons} actionButtonsDisplay={actionButtonsDisplay} actionMenuTriggerVariant={actionMenuTriggerVariant} getVisibleMenuItems={getVisibleMenuItems} renderMenuEntries={renderMenuEntries} horizontalOverflowState={horizontalOverflowState} showSelectionBoundary={showSelectionBoundary} stickyColumnLayouts={stickyColumnLayouts} variant={variant_0} rowStyle={getRowStyle?.(row_4.original, rowIndex_2)} hideDivider={nextVariant === "summary" || surface === "flush" && rowIndex_2 === table.getRowModel().rows.length - 1} />
      </React.Fragment>;
  }, [enableRowSelection, getRowHref, getRowTarget, getRowVariant, getRowStyle, hasClickHandler, isRowClickable, onRowClick, onCheckboxClick, actionButtons, actionButtonsDisplay, actionMenuTriggerVariant, getVisibleMenuItems, renderMenuEntries, horizontalOverflowState, showSelectionBoundary, stickyColumnLayouts, surface, table]);
  const firstDataHeaderId = table.getFlatHeaders()[0]?.id;
  const firstDataStickyLayout = firstDataHeaderId == null ? undefined : stickyColumnLayouts.get(firstDataHeaderId);
  const pinGroupLabelOnHorizontalScroll = firstDataStickyLayout?.side === "left";
  const renderGroup = useCallback((group_0: FilterListGroupedRows<$Row>[number], nested = false, groupIndex = 0) => {
    const isCollapsed = collapsedGroupKeys[group_0.key] ?? false;
    return <div key={group_0.key} className={cn("grid list-table-subgrid", styles.span)}>
        {groupIndex > 0 && <div aria-hidden="true" className={styles.groupGap} />}
        <div className={cn("grid list-table-subgrid", styles.groupHeader)} data-pinned={!nested || undefined}>
          <ListGroupHeader label={group_0.label} count={group_0.rows.length} collapsed={isCollapsed} onToggle={() => toggleGroup(group_0.key)} hasSelectionColumn={enableRowSelection} pinLabelOnHorizontalScroll={pinGroupLabelOnHorizontalScroll} nested={nested} />
        </div>
        {!isCollapsed && (group_0.children?.length ? group_0.children.map((childGroup, childGroupIndex) => renderGroup(childGroup, true, childGroupIndex)) : group_0.rows.map((row_5, groupRowIndex) => renderRow(row_5, rowIndexById.get(row_5.id) ?? 0, group_0.rows[groupRowIndex - 1]?.getIsSelected() ?? false, group_0.rows[groupRowIndex + 1]?.getIsSelected() ?? false)))}
      </div>;
  }, [collapsedGroupKeys, enableRowSelection, pinGroupLabelOnHorizontalScroll, renderRow, rowIndexById, toggleGroup]);
  const firstRightStickyHeaderId_0 = table.getFlatHeaders().find(header_3 => stickyColumnLayouts.get(header_3.id)?.side === "right")?.id;
  const groupedColumnHeaders = useMemo<ColumnGroupHeaderLayout[]>(() => {
    if (!columnHeaderGroups) return [];
    const tracks: Array<{
      label?: string;
      width: string;
      stickyOffset?: string;
    }> = [];
    if (enableRowSelection) tracks.push({
      width: "var(--list-selection-column-width)",
      stickyOffset: "var(--list-active-table-padding-inline)"
    });
    headers.forEach(header_4 => {
      if (header_4.id === firstRightStickyHeaderId_0) tracks.push({
        width: "12px"
      });
      const sticky = stickyColumnLayouts.get(header_4.id);
      tracks.push({
        label: columnHeaderGroups[header_4.column.id],
        width: `${header_4.getSize()}px`,
        stickyOffset: sticky?.side === "left" ? sticky.offset : undefined
      });
    });
    if (!firstRightStickyHeaderId_0) tracks.push({
      width: "12px"
    });
    if (actionButtons && actionButtonsDisplay === "sticky") tracks.push({
      width: `${actionColumnSize}px`
    });
    const extent = (start: number, count: number, trailingGap = false) => joinCssLengthParts(tracks.slice(start, start + count).flatMap((track, index_0) => index_0 < count - 1 || trailingGap ? [track.width, "var(--list-row-gap)"] : [track.width]));
    const firstMoving = tracks.findIndex(track_0 => !track_0.stickyOffset);
    const pinnedEdge = extent(0, firstMoving < 0 ? tracks.length : firstMoving);
    return getColumnHeaderGroups(tracks.map(track_1 => track_1.label)).map(group_1 => {
      const members = tracks.slice(group_1.start, group_1.start + group_1.span);
      const firstMovingMember = members.findIndex(track_2 => !track_2.stickyOffset);
      const pinnedCount = firstMovingMember < 0 ? members.length : firstMovingMember;
      if (pinnedCount) return {
        ...group_1,
        stickyOffset: members[0]!.stickyOffset,
        pinnedWidth: extent(group_1.start, pinnedCount)
      };
      const origin = extent(0, group_1.start, true);
      return {
        ...group_1,
        leadingClip: `clamp(0px, calc(var(--list-scroll-left, 0px) + ${pinnedEdge} - ${origin}), 100%)`,
        trailingClip: `clamp(0px, calc(${origin} + 100% - var(--list-scroll-left, 0px) - var(--list-viewport-width, 100vw) + var(--list-active-table-padding-inline) * 2), 100%)`
      };
    });
  }, [columnHeaderGroups, enableRowSelection, headers, columnSizing, firstRightStickyHeaderId_0, stickyColumnLayouts, actionButtons, actionButtonsDisplay, actionColumnSize]);
  const showActionsColumn = Boolean(actionButtons) && actionButtonsDisplay === "sticky";
  const isGrouped = groupedRows != null && !groupingOption?.preserveTableDividers;
  return <div className={cn("relative flex min-h-0 w-full flex-1 flex-col overflow-hidden", styles.surface)} data-inset={tableContentInset === "list" ? "list" : surface === "flush" || tableContentInset === "compact" ? "compact" : undefined}>
      {selectAllBanner && <div className="shrink-0">{selectAllBanner}</div>}
      <ScrollArea orientation="both" className="min-h-0 w-full flex-1 overflow-auto" viewportClassName="overscroll-x-contain" viewportProps={{
      "data-list-scroll-viewport": "table"
    }} viewportRef={viewportRef} horizontalScrollbarRegion={horizontalScrollbarRegion === "unpinned" ? unpinnedScrollbarRegion : undefined}>
        <div data-list-table-grid="true" data-selectable={enableRowSelection || undefined} data-grouped={isGrouped || undefined} className={styles.table} style={{
        "--list-grid-template-columns": gridTemplateColumns,
        "--list-viewport-width": viewportClientWidth ? `${viewportClientWidth}px` : undefined
      } as React.CSSProperties}>
          <FilterListHeader table={table} enableRowSelection={enableRowSelection} isAllSelected={isAllSelected} onClearSelection={onClearSelection} stickyColumnLayouts={stickyColumnLayouts} horizontalOverflowState={horizontalOverflowState} showSelectionBoundary={showSelectionBoundary} columnHeaderGroups={columnHeaderGroups} groupedColumnHeaders={groupedColumnHeaders} showActionsColumn={showActionsColumn} />

          {table.getRowModel().rows.length > 0 ? groupedRows ? groupedRows.map((group_2, groupIndex_0) => renderGroup(group_2, false, groupIndex_0)) : table.getRowModel().rows.map((row_6, rowIndex_3, rows_0) => renderRow(row_6, rowIndex_3, rows_0[rowIndex_3 - 1]?.getIsSelected() ?? false, rows_0[rowIndex_3 + 1]?.getIsSelected() ?? false)) : <ListSurfaceState state={error ? "error" : isLoading ? "loading" : "empty"} isRetrying={isRetrying} onRetry={onRetry} />}
          <div ref={bottomRef} className={table.getRowModel().rows.some(row_7 => getRowVariant?.(row_7.original) === "summary") ? styles.span : styles.clearance} />
          {isFetchingMore && <div className={cn("flex flex-row justify-center py-4", styles.viewportCentered)}>
              <Spinner size="md" tone="faint" />
            </div>}
        </div>
      </ScrollArea>
      {horizontalOverflowState.hasScrolledLeft && <div aria-hidden="true" className={styles.scrollAffordance} data-list-scroll-affordance="left" />}
      {horizontalOverflowState.hasScrollableRight && <div aria-hidden="true" className={styles.scrollAffordance} data-list-scroll-affordance="right" />}
    </div>;
}
export default function FilterListComponent<$Row, $Filters extends FilterFormValues>({
  table,
  getRowHref,
  getRowTarget,
  onClickRow,
  actionMenuItems,
  actionButtons,
  actionButtonsDisplay,
  actionColumnSize,
  actionMenuTriggerVariant,
  card,
  selectActions,
  isLoading,
  isRefreshing = false,
  error,
  isRetrying = false,
  onRetry,
  onFetchMore,
  hasMore,
  isFetchingMore,
  exportMutation,
  immediateSelectedExport,
  viewMode = "table",
  groupingOption,
  subGroupingOption,
  listOptionState,
  currentFilters,
  currentSearch,
  toolbarSlot,
  surface = "framed",
  tableContentInset = "default",
  countQuery,
  pagination,
  selectionScopeKey,
  showSelectionControls,
  selectedRows,
  stickyColumns,
  horizontalScrollbarRegion,
  flexibleColumnId,
  columnHeaderGroups,
  getRowVariant,
  getRowStyle,
  isRowClickable
}: FilterListProps<$Row, $Filters> & {
  table: ReturnType<typeof useReactTable<$Row>>;
  selectedRows?: $Row[];
  toolbarSlot?: ReactNode;
}) {
  "use no memo";

  const enableRowSelection = table.options.enableRowSelection !== false;
  const lastSelectedRowIndexRef = useRef<number | null>(null);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isExportDownloading, setIsExportDownloading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const loadedRowCount = table.getRowModel().rows.length;
  const selectableRows = table.getRowModel().rows.filter(row => row.getCanSelect());
  const selectableRowCount = selectableRows.length;
  const selectedRowCount = selectedRows?.length ?? table.getSelectedRowModel().rows.length;
  const totalCount = countQuery?.data;
  const isPaged = pagination?.mode === "paged";
  const allLoadedSelected = selectableRowCount > 0 && selectableRows.every(row_0 => row_0.getIsSelected());
  const hasMoreResults = hasMore || totalCount != null && totalCount > loadedRowCount;
  const showSelectAllBanner = enableRowSelection && allLoadedSelected && hasMoreResults && !isAllSelected;
  const retainedDataAlert = error && loadedRowCount > 0 ? <Alert className={cn(styles.alert, "my-2 flex items-center justify-between")} tone="danger">
        <span>Couldn&apos;t refresh this list. Your previous results are still shown.</span>
        {onRetry && <Button disabled={isRetrying} onClick={onRetry} variant="danger">
            <RotateCcw />
            {isRetrying ? "Retrying..." : "Retry"}
          </Button>}
      </Alert> : null;
  const getSelectedRows = useCallback(() => {
    return selectedRows ?? table.getSelectedRowModel().rows.map(row_1 => row_1.original);
  }, [selectedRows, table]);
  const getSelectionTarget = useCallback((): SelectionTarget<$Row> => {
    if (isAllSelected) {
      return {
        type: "all"
      };
    }
    return {
      type: "rows",
      rows: getSelectedRows()
    };
  }, [isAllSelected, getSelectedRows]);

  // When isAllSelected, auto-select newly loaded rows (from infinite scroll)
  useEffect(() => {
    if (!isAllSelected) return;
    const rowSelection = table.getState().rowSelection;
    const rows = table.getRowModel().rows.filter(row_2 => row_2.getCanSelect());
    const allSelected = rows.every(row_3 => !!rowSelection[row_3.id]);
    if (!allSelected) {
      const newSelection: RowSelectionState = {};
      rows.forEach(row_4 => {
        newSelection[row_4.id] = true;
      });
      table.setRowSelection(newSelection);
    }
  }, [isAllSelected, loadedRowCount, table, table.options.data]);
  useEffect(() => {
    setIsAllSelected(false);
    lastSelectedRowIndexRef.current = null;
  }, [selectionScopeKey]);
  const clearSelection = useCallback(() => {
    setIsAllSelected(false);
    table.setRowSelection({});
    lastSelectedRowIndexRef.current = null;
  }, [table]);

  // Selection handler — click sets anchor, shift+click selects range from anchor.
  // Uses ref for anchor so the callback is stable (no re-render on selection).
  const handleRowSelect = useCallback((clickedRowIndex: number, shiftKey: boolean) => {
    setIsAllSelected(false);
    const anchor = lastSelectedRowIndexRef.current;
    if (shiftKey && anchor !== null && enableRowSelection) {
      const start = Math.min(anchor, clickedRowIndex);
      const end = Math.max(anchor, clickedRowIndex);
      const rows_0 = table.getRowModel().rows;
      table.setRowSelection(prev => {
        const next = {
          ...prev
        };
        for (let i = start; i <= end; i++) {
          const row_5 = rows_0[i];
          if (row_5?.getCanSelect()) next[row_5.id] = true;
        }
        return next;
      });
    } else if (enableRowSelection) {
      const row_6 = table.getRowModel().rows[clickedRowIndex];
      if (row_6?.getCanSelect()) {
        row_6.toggleSelected();
        lastSelectedRowIndexRef.current = clickedRowIndex;
      }
    }
  }, [table, enableRowSelection]);
  useEffect(() => {
    if (isPaged || !onFetchMore || !hasMore) return;
    const observer = new IntersectionObserver(entries => {
      const firstEntry = entries[0];
      if (firstEntry?.isIntersecting && hasMore && !isFetchingMore) {
        onFetchMore();
      }
    }, {
      root: viewportRef.current,
      rootMargin: "0px 0px 320px 0px",
      threshold: 0
    });
    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }
    return () => {
      if (bottomRef.current) observer.unobserve(bottomRef.current);
    };
  }, [isPaged, onFetchMore, hasMore, isFetchingMore]);
  const selectionControls = showSelectionControls && enableRowSelection || showSelectAllBanner || isAllSelected ? <ListSelectionHeader inset={surface === "flush" && tableContentInset !== "list" ? "flush" : "list"} label={isAllSelected ? `All ${totalCount?.toLocaleString("en-US") ?? "matching"} results selected` : `${selectedRowCount.toLocaleString("en-US")} selected`}>
        <Button variant="secondary" disabled={selectableRowCount === 0 || isAllSelected || allLoadedSelected} onClick={() => table.toggleAllRowsSelected(true)}>
          Select page ({selectableRowCount})
        </Button>
        {hasMoreResults && <Button variant="secondary" disabled={isAllSelected || selectableRowCount === 0} onClick={() => {
      table.toggleAllRowsSelected(true);
      setIsAllSelected(true);
    }}>
            Select all matching{totalCount != null ? ` (${totalCount.toLocaleString("en-US")})` : ""}
          </Button>}
        {(selectedRowCount > 0 || isAllSelected) && <Button variant="ghost" onClick={clearSelection}>
            Clear selection
          </Button>}
      </ListSelectionHeader> : undefined;
  return <>
      <div aria-busy={isLoading || isRefreshing} data-selection-open={enableRowSelection && (selectedRowCount > 0 || isAllSelected) || undefined} className={cn("flex-1 w-full flex flex-col min-h-0 group overflow-hidden relative", styles.root, !card && surface === "framed" && "group-data-[padded=true]/dashboard-screen-body:border group-data-[padded=true]/dashboard-screen-body:rounded-lg")} data-list-query-state={error ? "error" : isLoading ? "loading" : isRefreshing ? "refreshing" : "settled"}>
        <RouteReadyTelemetry enabled={!isLoading && !isRefreshing && !error} signal="list_content_ready" />
        {viewMode === "table" ? <>
              {toolbarSlot}
              {showSelectionControls && selectionControls}
              {retainedDataAlert}

              <FilterListTableSurface table={table} enableRowSelection={enableRowSelection} getRowHref={getRowHref} getRowTarget={getRowTarget} onClickRow={onClickRow} handleRowSelect={handleRowSelect} actionButtons={actionButtons} actionButtonsDisplay={actionButtonsDisplay} actionColumnSize={actionColumnSize} actionMenuTriggerVariant={actionMenuTriggerVariant} actionMenuItems={actionMenuItems} isLoading={isLoading} error={error} isRetrying={isRetrying} onRetry={onRetry} bottomRef={bottomRef} viewportRef={viewportRef} isFetchingMore={isFetchingMore} groupingOption={groupingOption} subGroupingOption={subGroupingOption} listOptionState={listOptionState} isAllSelected={isAllSelected} onClearSelection={clearSelection} columnHeaderGroups={columnHeaderGroups} stickyColumns={stickyColumns} horizontalScrollbarRegion={horizontalScrollbarRegion} flexibleColumnId={flexibleColumnId} getRowVariant={getRowVariant} getRowStyle={getRowStyle} isRowClickable={isRowClickable} surface={surface} tableContentInset={tableContentInset} selectAllBanner={showSelectionControls ? undefined : selectionControls} />
              {pagination && <Pagination count={pagination.count} pagination={pagination.pagination} pageSizeOptions={pagination.pageSizeOptions} />}
            </> : <>
              {retainedDataAlert}
              <FilterListGridSurface isLoading={isLoading} isRowClickable={isRowClickable} table={table} enableRowSelection={enableRowSelection} getRowHref={getRowHref} getRowTarget={getRowTarget} onClickRow={onClickRow} handleRowSelect={handleRowSelect} card={card} bottomRef={bottomRef} viewportRef={viewportRef} error={error} isRetrying={isRetrying} onRetry={onRetry} isFetchingMore={isFetchingMore} />
              {pagination && <Pagination count={pagination.count} pagination={pagination.pagination} pageSizeOptions={pagination.pageSizeOptions} />}
            </>}
        {enableRowSelection && <ListSelectionBar open={selectedRowCount > 0 || isAllSelected} onClear={clearSelection} count={isAllSelected && totalCount != null ? Intl.NumberFormat("en-US").format(totalCount) : selectedRowCount}>
              {selectActions?.filter(action => !(isAllSelected && action.supportsAll === false)).reduce<FilterSelectActionInterface<$Row>[][]>((groups, action_0) => {
          const previous = groups[groups.length - 1];
          if (previous && previous[0]?.group === action_0.group) previous.push(action_0);else groups.push([action_0]);
          return groups;
        }, []).map(actions => <ListSelectionBarGroup key={actions[0]?.label}>
                    {actions.map(action_1 => <ListSelectionBarAction key={action_1.label} disabled={action_1.isDisabled || action_1.isLoading} destructive={action_1.variant === "destructive"} icon={action_1.icon} isLoading={action_1.isLoading} onClick={() => action_1.onClick(getSelectionTarget(), clearSelection)}>
                        {action_1.label}
                      </ListSelectionBarAction>)}
                  </ListSelectionBarGroup>)}
              {(exportMutation || immediateSelectedExport) && <>
                  <Separator orientation="vertical" className="h-full bg-border-secondary" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <ListSelectionBarAction layout="segment" icon={<Download />} isLoading={exportMutation?.isPending || immediateSelectedExport?.isPending || isExportDownloading} disabled={exportMutation?.isPending || immediateSelectedExport?.isPending || isExportDownloading}>
                        Export
                      </ListSelectionBarAction>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {(["csv", "xlsx", "pdf"] as const).map(format => <DropdownMenuItem key={format} onClick={() => {
                if (isExportDownloading) return;
                const target = getSelectionTarget();
                const exportTarget = target.type === "all" ? {
                  type: "all" as const,
                  filters: currentFilters,
                  search: currentSearch
                } : {
                  type: "ids" as const,
                  ids: Object.keys(table.getState().rowSelection).filter(id => table.getState().rowSelection[id])
                };
                const immediateDownload = exportTarget.type === "ids" && immediateSelectedExport ? immediateSelectedExport.download({
                  format,
                  ids: exportTarget.ids
                }) : null;
                if (!immediateDownload && !exportMutation) return;
                const download = (async () => {
                  setIsExportDownloading(true);
                  try {
                    if (immediateDownload) {
                      await immediateDownload;
                    } else {
                      const delivery = await exportMutation!.mutateAsync({
                        format,
                        exportTarget
                      });
                      await downloadTableExport(delivery);
                    }
                  } finally {
                    setIsExportDownloading(false);
                  }
                })();
                toast.promise(download, {
                  loading: `Preparing ${target.type === "all" ? "all" : `${target.rows.length}`} as ${format.toUpperCase()}...`,
                  success: "Download started",
                  error: "Couldn't export. Try again."
                });
              }}>
                          Export {isAllSelected ? "all" : "selected"} as {format === "xlsx" ? "XLS" : format.toUpperCase()}
                        </DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>}
          </ListSelectionBar>}
      </div>
    </>;
}